import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

const CLEAR_EVENT = "unread-badges:clear-channel";
const BC_NAME = "unread-badges";
/**
 * After a clear (local or via BroadcastChannel) we suppress that channel's
 * unread count for this window to absorb the lag between the optimistic
 * clear and the `channel_reads` realtime/recompute round-trip. This prevents
 * a transient "1 → 0 → 1 → 0" badge flicker.
 */
const CLEAR_SUPPRESSION_MS = 2500;
/** Coalesce bursts of realtime events into a single recompute. */
const RECOMPUTE_DEBOUNCE_MS = 150;

function getBroadcast(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(BC_NAME);
  } catch {
    return null;
  }
}

export function clearChannelUnread(channelId: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CLEAR_EVENT, { detail: { channelId } }));
  const bc = getBroadcast();
  if (bc) {
    try { bc.postMessage({ type: "clear", channelId }); } catch { /* ignore */ }
    try { bc.close(); } catch { /* ignore */ }
  }
}

export type UnreadBadges = {
  /** Total unread across non-muted channels (for the bottom-nav badge). */
  messages: number;
  /** Unread count per channel (includes muted channels — UI may still show a dot). */
  perChannel: Record<string, number>;
  /** Set of channel IDs that are effectively muted for this user. */
  muted: Set<string>;
};

/**
 * Live unread message counts for the current user.
 * Muted channels are excluded from the global `messages` total but their
 * per-channel counts remain accurate so the conversation list can still
 * show a dot/number with a muted indicator.
 */
export function useUnreadBadges(): UnreadBadges {
  const { user, currentOrg } = useAuth();
  const [perChannel, setPerChannel] = useState<Record<string, number>>({});
  const [muted, setMuted] = useState<Set<string>>(() => new Set());
  // Channels recently cleared (id → timestamp ms). Reads are suppressed to 0
  // until the suppression window expires, regardless of what realtime
  // recompute returns.
  const recentlyClearedRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!user || !currentOrg) {
      setPerChannel({});
      setMuted(new Set());
      return;
    }
    let cancelled = false;
    const orgId = currentOrg.id;
    const userId = user.id;

    const recomputeMuted = async (channelIds: string[], kindByChannel: Record<string, string>) => {
      // Per-user explicit prefs win.
      const { data: userPrefs } = await supabase
        .from("channel_notification_prefs")
        .select("channel_id, muted, muted_until")
        .eq("user_id", userId)
        .in("channel_id", channelIds.length ? channelIds : ["00000000-0000-0000-0000-000000000000"]);

      const explicit = new Map<string, boolean>();
      (userPrefs ?? []).forEach((p) => {
        const until = (p as { muted_until?: string | null }).muted_until;
        const stillMuted = !until || new Date(until).getTime() > Date.now();
        explicit.set(p.channel_id as string, !!p.muted && stillMuted);
      });

      // Role defaults for any channel without an explicit pref.
      const { data: roles } = await supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", orgId)
        .eq("user_id", userId);
      const userRoles = (roles ?? []).map((r) => r.role as string);

      let roleMutedKinds = new Set<string>();
      if (userRoles.length) {
        const { data: roleDefaults } = await supabase
          .from("role_notification_prefs")
          .select("role, channel_kind, muted")
          .eq("organization_id", orgId)
          .in("role", userRoles as never[]);
        roleMutedKinds = new Set(
          (roleDefaults ?? [])
            .filter((r) => r.muted)
            .map((r) => r.channel_kind as string),
        );
      }

      const mutedSet = new Set<string>();
      for (const cid of channelIds) {
        if (explicit.has(cid)) {
          if (explicit.get(cid)) mutedSet.add(cid);
        } else if (roleMutedKinds.has(kindByChannel[cid] ?? "")) {
          mutedSet.add(cid);
        }
      }
      if (!cancelled) setMuted(mutedSet);
    };

    const recompute = async () => {
      const { data: members } = await supabase
        .from("channel_members")
        .select("channel_id, message_channels:channel_id(kind)")
        .eq("user_id", userId)
        .eq("organization_id", orgId);

      const channelIds: string[] = [];
      const kindByChannel: Record<string, string> = {};
      (members ?? []).forEach((m) => {
        const cid = m.channel_id as string;
        channelIds.push(cid);
        const kind = (m as { message_channels?: { kind?: string } | null }).message_channels?.kind;
        if (kind) kindByChannel[cid] = kind;
      });

      if (channelIds.length === 0) {
        if (!cancelled) {
          setPerChannel({});
          setMuted(new Set());
        }
        return;
      }

      const { data: reads } = await supabase
        .from("channel_reads")
        .select("channel_id, last_read_message_id")
        .eq("user_id", userId)
        .in("channel_id", channelIds);

      const readMap = new Map<string, number>();
      (reads ?? []).forEach((r) =>
        readMap.set(r.channel_id as string, Number(r.last_read_message_id ?? 0)),
      );

      const counts: Record<string, number> = {};
      const now = Date.now();
      // Drop expired suppression entries.
      for (const [cid, ts] of recentlyClearedRef.current) {
        if (now - ts > CLEAR_SUPPRESSION_MS) recentlyClearedRef.current.delete(cid);
      }
      await Promise.all(
        channelIds.map(async (cid) => {
          const lastRead = readMap.get(cid) ?? 0;
          const { count } = await supabase
            .from("messages")
            .select("id", { count: "exact", head: true })
            .eq("channel_id", cid)
            .gt("id", lastRead)
            .neq("sender_id", userId);
          // If this channel was just cleared locally/cross-tab, force 0
          // until the suppression window elapses to absorb realtime lag.
          counts[cid] = recentlyClearedRef.current.has(cid) ? 0 : (count ?? 0);
        }),
      );
      if (!cancelled) setPerChannel(counts);
      await recomputeMuted(channelIds, kindByChannel);
    };

    let recomputeTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRecompute = () => {
      if (recomputeTimer) clearTimeout(recomputeTimer);
      recomputeTimer = setTimeout(() => {
        recomputeTimer = null;
        recompute();
      }, RECOMPUTE_DEBOUNCE_MS);
    };

    recompute();

    const ch = supabase
      .channel(`unread-badges-${userId}-${Math.random().toString(36).slice(2, 10)}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `organization_id=eq.${orgId}` },
        () => scheduleRecompute(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "channel_reads", filter: `user_id=eq.${userId}` },
        () => scheduleRecompute(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "channel_notification_prefs", filter: `user_id=eq.${userId}` },
        () => scheduleRecompute(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "role_notification_prefs", filter: `organization_id=eq.${orgId}` },
        () => scheduleRecompute(),
      )
      .subscribe();

    const clearLocal = (channelId: string) => {
      // Mark this channel as recently cleared so any in-flight or
      // about-to-arrive recompute is forced to 0 for the suppression window.
      recentlyClearedRef.current.set(channelId, Date.now());
      setPerChannel((prev) =>
        prev[channelId] ? { ...prev, [channelId]: 0 } : prev,
      );
    };
    const onClear = (e: Event) => {
      const detail = (e as CustomEvent<{ channelId: string }>).detail;
      if (detail?.channelId) clearLocal(detail.channelId);
    };
    window.addEventListener(CLEAR_EVENT, onClear);

    const bc = getBroadcast();
    const onBcMessage = (ev: MessageEvent) => {
      const data = ev.data as { type?: string; channelId?: string } | null;
      if (data?.type === "clear" && data.channelId) clearLocal(data.channelId);
    };
    if (bc) bc.addEventListener("message", onBcMessage);

    return () => {
      cancelled = true;
      if (recomputeTimer) clearTimeout(recomputeTimer);
      supabase.removeChannel(ch);
      window.removeEventListener(CLEAR_EVENT, onClear);
      if (bc) {
        bc.removeEventListener("message", onBcMessage);
        try { bc.close(); } catch { /* ignore */ }
      }
    };
  }, [user?.id, currentOrg?.id]);

  const messages = Object.entries(perChannel).reduce(
    (acc, [cid, n]) => acc + (muted.has(cid) ? 0 : n),
    0,
  );
  return { messages, perChannel, muted };
}
